function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  const isPanelTrigger = summary.closest('.panel-trigger');
  summary.setAttribute('role', 'button');
  if (!isPanelTrigger) {
    summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));
  }

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    if (
      event.currentTarget.parentElement.parentElement &&
      event.currentTarget.parentElement.parentElement.classList.contains('product__accordion')
    ) {
      event.preventDefault();
    } else if (!isPanelTrigger) {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    }
    // event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

document.querySelectorAll('.menu-drawer__submenu').forEach((submenu) => {
  submenu.addEventListener('mouseleave', this.onSubmenuMouseLeave.bind(this));
});

function onSubmenuMouseLeave(event) {
  const submenu = event.currentTarget;
  const detailsElement = submenu.parentNode;
  const summaryElement = detailsElement.querySelector('summary');
  const animatedElements = detailsElement.querySelectorAll('.scroll-trigger.animate--slide-in');
  animatedElements.forEach((element) => {
    element.classList.remove('animate--slide-in', 'scroll-trigger--offscreen');
    void element.offsetWidth;
  });
  setTimeout(() => {
    detailsElement.classList.remove('menu-opening');
    detailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.classList.remove('submenu-open');
    const parentMenuElement = detailsElement.closest('.has-submenu');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
  }, 100);
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => {
      if (window.matchMedia('(min-width: 990px)').matches) {
        if (summary.classList.contains('font-menu')) {
          summary.addEventListener('mouseover', this.onSummaryMouseOver.bind(this));
          summary.addEventListener('mouseout', this.onSummaryMouseOut.bind(this));
        } else {
          summary.addEventListener('click', this.onSummaryClick.bind(this));
        }
      } else {
        summary.addEventListener('click', this.onSummaryClick.bind(this));
      }
    });
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryMouseOut(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const submenu = detailsElement.querySelector('.menu-drawer__submenu');

    const isHoveringSubmenu = submenu && submenu.contains(event.relatedTarget);

    if (!isHoveringSubmenu) {
      setTimeout(() => {
        detailsElement.classList.remove('menu-opening');
        detailsElement.removeAttribute('open');
        summaryElement.setAttribute('aria-expanded', false);
        summaryElement.classList.remove('submenu-open');
        parentMenuElement && parentMenuElement.classList.remove('submenu-open');
        const animatedElements = detailsElement.querySelectorAll('.scroll-trigger.animate--slide-in');
        animatedElements.forEach((element) => {
          element.classList.remove('animate--slide-in', 'scroll-trigger--offscreen');
          void element.offsetWidth;
        });
      }, 100);
    }
  }

  onSummaryMouseOver(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const animatedElements = detailsElement.querySelectorAll('.scroll-trigger');
    animatedElements.forEach((element) => {
      element.classList.add('animate--slide-in');
    });
    setTimeout(() => {
      detailsElement.classList.add('menu-opening');
      detailsElement.setAttribute('open', '');
      summaryElement.setAttribute('aria-expanded', true);
      summaryElement.classList.add('submenu-open');
    }, 100);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        // parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
        if (event.srcElement.classList.contains('font-menu')) {
          const hideElements = document.querySelectorAll('.hide');

          hideElements.forEach((element) => {
            element.classList.remove('hide');
          });
        }
      }, 50);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    }, 200);
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty('--header-bottom-position', `0px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty('--header-bottom-position', `0px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.target.closest('.media-zoom-button, .thumbnail-item, .media-inner-container')) return;

        if (window.matchMedia('(min-width: 750px)').matches) {
          if (
            event.target.classList.contains('slider-button--next') ||
            event.target.parentElement?.classList.contains('slider-button--next') ||
            event.target.parentElement?.parentElement?.classList.contains('slider-button--next')
          ) {
            this.updateThumbnailState();
          }
          if (
            event.target.classList.contains('slider-button--prev') ||
            event.target.parentElement?.classList.contains('slider-button--prev') ||
            event.target.parentElement?.parentElement?.classList.contains('slider-button--prev')
          ) {
            this.updateThumbnailState();
          }
          if (event.target.classList == '') {
            return;
          }
        } else {
          if (event.pointerType === 'mouse' && event.target === this) {
            this.hide();
          }
        }
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

    if (window.matchMedia('(min-width: 750px)').matches) {
      this.updateThumbnailState();
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    window.pauseAllMedia();
  }

  updateThumbnailState() {
    // Use requestAnimationFrame instead of setTimeout for better performance
    requestAnimationFrame(() => {
      const activeMedias = this.querySelectorAll('.product-media-item');
      const prevBtn = this.querySelector('.slider-button--prev');
      const nextBtn = this.querySelector('.slider-button--next');
      const totalItems = activeMedias.length;

      // Cache active media element to avoid querying DOM multiple times
      const activeMedia = Array.from(activeMedias).find((media) => media.classList.contains('active'));

      if (activeMedia) {
        const index = activeMedia.getAttribute('data-index');

        // Update thumbnails
        this.goToSlide(index);

        // Update button visibility
        const isFirst = index === '0';
        const isLast = index === (totalItems - 1).toString();
        prevBtn.style.display = isFirst ? 'none' : 'block';
        nextBtn.style.display = isLast ? 'none' : 'block';
      }

      // Use event delegation instead of adding listeners to each thumbnail
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('.thumbnail-item');
        if (thumb) {
          const targetIndex = thumb.getAttribute('data-index');
          this.goToSlide(targetIndex);
        }
      });
    });
  }

  goToSlide(index) {
    const thumbnailMedias = this.querySelectorAll('.thumbnail-item');

    thumbnailMedias.forEach((thumbnail) => {
      const thumbIndex = thumbnail.getAttribute('data-index');
      thumbnail.classList.toggle('active', thumbIndex === index);
    });
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);

      // image display none
      this.querySelector('.deferred-media__poster').classList.add('d-none');
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.counterElements = this.querySelectorAll('.counter-element');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;

      const currentCounterElement = this.querySelector(`[data-item="${this.currentPage}"]`);
      currentCounterElement && currentCounterElement.style.setProperty('background', '#34292C');
      if (this.counterElements) {
        this.counterElements.forEach((el) => {
          if (el.getAttribute('data-item') !== this.currentPage.toString()) {
            el.style.setProperty('background', '#B3ACA6');
          }
        });
      }
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      if (this.sliderItemsToShow[0].classList.contains('complementary-slide')) return;
      this.prevButton.setAttribute('disabled', 'disabled');
      this.prevButton.classList.add('d-none');
    } else {
      this.prevButton.removeAttribute('disabled');
      this.prevButton.classList.remove('d-none');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      if (this.sliderItemsToShow[this.sliderItemsToShow.length - 1].classList.contains('complementary-slide')) return;
      this.nextButton.setAttribute('disabled', 'disabled');
      this.nextButton.classList.add('d-none');
    } else {
      this.nextButton.removeAttribute('disabled');
      this.nextButton.classList.remove('d-none');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.productsData = [];
    this.selectedVariant = JSON.parse(this.querySelector('script[data-selected-variant]')?.innerHTML?.trim());
    this.handleOptionClick = this.handleOptionClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });

    this.initCustomSelects();
    this.loadVariantOptions();
  }

  initCustomSelects() {
    // delegate click events to the custom select
    this.addEventListener('click', (e) => {
      const option = e.target.closest('.option:not(.out-of-stock)');
      if (!option) return;

      this.handleOptionClick(e, option);
    });

    // toggle dropdown on click
    this.querySelectorAll('.selected-option').forEach((selectedOption) => {
      selectedOption.addEventListener('click', (e) => {
        const customSelect = e.currentTarget.closest('.custom-select');
        customSelect.classList.toggle('active');
        this.querySelector('.icon-vector').style.transform = customSelect.classList.contains('active')
          ? 'rotate(180deg)'
          : 'rotate(0deg)';
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.custom-select')) {
        this.querySelectorAll('.custom-select').forEach((select) => {
          select.classList.remove('active');
          this.querySelector('.icon-vector').style.transform = select.classList.contains('active')
            ? 'rotate(180deg)'
            : 'rotate(0deg)';
        });
      }
    });
  }

  handleOptionClick(e, option) {
    const customSelect = option.closest('.custom-select');
    const nativeSelect = this.querySelector('.select__select');
    const selectedOption = customSelect.querySelector('.selected-option');
    const optionValue = option.dataset.optionValueId || option.textContent.trim();

    // 1. update the native select
    if (nativeSelect) {
      const optionToSelect = Array.from(nativeSelect.options).find(
        (opt) => opt.value === optionValue || opt.textContent.trim() === option.textContent.trim(),
      );

      if (optionToSelect) {
        optionToSelect.setAttribute('selected', 'selected');
        nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 2. update the selected option
    if (selectedOption) {
      const selectedText = selectedOption.querySelector('.selected-text');
      if (selectedText) {
        selectedText.textContent = option.textContent.replace(' - Out of stock', '').trim();
      }

      // update the swatch
      const optionSwatch = option.querySelector('.option-swatch');
      const selectedSwatch = selectedOption.querySelector('.dropdown-swatch .swatch');
      if (optionSwatch && selectedSwatch) {
        selectedSwatch.style.cssText = optionSwatch.style.cssText;
      }
    }

    // 3. close the custom select
    customSelect.classList.remove('active');
  }

  updateCustomSelectDisplay(select) {
    if (!select.classList.contains('native-select')) return;
    // create div element with the option attributes and push it to my custom select
    const customSelect = select.closest('.select').querySelector('.custom-select');
    const options = customSelect.querySelector('.options');
    options.innerHTML = '';
    const selectedText = this.querySelector('.selected-text');

    select.querySelectorAll('option').forEach((option) => {
      if (option.value.toUpperCase() === selectedText.textContent.toUpperCase()) {
        return;
      }
      const optionElement = document.createElement('div');
      optionElement.classList.add('option');
      optionElement.textContent = option.textContent;
      optionElement.dataset.optionValueId = option.value;
      optionElement.dataset.handle = option.dataset.handle;

      if (option.disabled) {
        optionElement.classList.add('out-of-stock');
      }

      if (option.dataset.optionSwatchValue) {
        if (optionElement.classList.contains('out-of-stock')) {
          const soldOutText = this.querySelector('.variant-picker-sold-out');
          optionElement.textContent = optionElement.textContent
            .toLocaleLowerCase()
            .replace(` - ${soldOutText?.textContent?.trim().toLocaleLowerCase()}`, '');
          optionElement.innerHTML = `<span class="option-swatch swatch" style="background-image: url(${
            option.dataset.optionSwatchValue
          }),  linear-gradient(#f8f8f800, #ebe6e2);"></span> ${optionElement.textContent.toUpperCase()}`;
          const outOfStockText = document.createElement('span');
          outOfStockText.classList.add('text-out-of-stock');
          outOfStockText.textContent = `${soldOutText?.textContent}`;
          optionElement.appendChild(outOfStockText);
        } else {
          optionElement.innerHTML = `<span class="option-swatch swatch" style="background-image: url(${option.dataset.optionSwatchValue}),  linear-gradient(#f8f8f800, #ebe6e2);"></span> ${option.textContent}`;
        }
      }

      // append the optionElement to the custom select
      options.appendChild(optionElement);
    });

    // add variants count
    let variantCount = select.querySelectorAll('option').length - 1;
    const notAvailableCount = select.querySelectorAll('option:disabled').length;
    variantCount -= notAvailableCount;
    if (variantCount > 0) {
      const newVariantCountElement = document.createElement('div');
      newVariantCountElement.classList.add('variant-count');
      const variantPickerText = this.querySelector('.variant-picker-text');
      newVariantCountElement.textContent = `+ ${variantCount} ${variantPickerText?.textContent}`;
      // append to selected-option element
      const selectedOption = this.querySelector('.custom-select')?.querySelector('.selected-option');
      selectedOption?.appendChild(newVariantCountElement);
    } else {
      // add d-none class to the custom select
      customSelect.parentElement.classList.add('d-none');
    }

    this.querySelector('.custom-select-wrapper').classList.remove('d-none');
  }

  async loadVariantOptions() {
    try {
      this.productsData = await this.fetchProductsData();
      if (this.productsData.length > 0) this.updateVariantOptions();
    } catch (error) {
      console.error('Failed to load variant options:', error);
    }
  }

  updateVariantOptions() {
    const pillFieldset = this.querySelector('.pills');
    const dropdownSelect = this.querySelector('.select__select');

    pillFieldset.innerHTML = '';

    const allVariants = this.productsData.flatMap((product) => product.variants);

    const sizeGroups = {};
    const scentOptions = new Set();

    allVariants.forEach((variant) => {
      const id = variant.id;
      const quantity = this.querySelector(`input[data-variant-id="${id}"]`)?.getAttribute('data-quantity');
      const available = quantity
        ? parseInt(quantity) > parseInt(window.product_min_stock.value) && variant.available
        : variant.available;
      if (!sizeGroups[variant.option1]) {
        sizeGroups[variant.option1] = { available: available, scents: new Set() };
      }
      sizeGroups[variant.option1].available = sizeGroups[variant.option1].available || available;

      sizeGroups[variant.option1].scents.add(variant.option2);
      scentOptions.add(variant.option2);
    });

    const initialSelected = { ...this.selectedVariant };

    const allSizes = Object.keys(sizeGroups).sort((a, b) => {
      const numA = parseFloat(a.match(/\d+/)?.[0]) || Infinity;
      const numB = parseFloat(b.match(/\d+/)?.[0]) || Infinity;
      return numA - numB || a.localeCompare(b);
    });

    const selectedSize = allSizes.find((size) => size === initialSelected.option1);

    if (scentOptions.size > 1) {
      scentOptions.forEach((scent) => {
        const variant = allVariants.find((v) => {
          return v.option2 === scent && v.option1 === selectedSize;
        });

        if (!variant) return;
        const isSelected = variant.option2 === initialSelected.option2;
        if (isSelected) {
          dropdownSelect?.querySelectorAll('option')?.forEach((option) => {
            if (option.value === scent) {
              // keep the selected variant in the URL so "sell when out of stock" variants don't fall back to the first in-stock one
              option.dataset.handle = `${variant.handle}?variant=${variant.id}`;
              return;
            }
          });
          return;
        }

        const option = document.createElement('option');
        option.value = scent;
        option.textContent = scent.toUpperCase();
        option.dataset.optionValueId = variant.id;
        // keep the selected variant in the URL so "sell when out of stock" variants don't fall back to the first in-stock one
        option.dataset.handle = `${variant.handle}?variant=${variant.id}`;
        option.dataset.optionSwatchValue = this.getSwatchUrlForScent(variant.id);

        dropdownSelect?.appendChild(option);
      });

      dropdownSelect?.dispatchEvent(new Event('change'));
    } else {
      dropdownSelect.parentElement.classList.add('d-none');
    }

    allSizes.forEach((size) => {
      const variant = allVariants.find((v) => v.option1 === size);
      if (!variant) return;

      const isSelected = variant.option1 === initialSelected.option1;
      const { available } = sizeGroups[size];

      const inputId = `${this.id}-size-${size.replace(/\s+/g, '-')}`;

      const input = document.createElement('input');
      input.type = 'radio';
      input.id = inputId;
      input.name = 'Size-1';
      const translatedSize = this.getTranslatedSize(size);
      input.value = translatedSize;
      input.dataset.optionValueId = variant.id;
      if (scentOptions.size > 1) {
        dropdownSelect?.querySelectorAll('option')?.forEach((option) => {
          const scent = option.value;
          const scentVariant = allVariants.find((v) => v.option1 === size && v.option2 === scent);
          if (option.selected === true) {
            if (scentVariant && scentVariant.available) {
              // keep the selected variant in the URL so "sell when out of stock" variants don't fall back to the first in-stock one
              input.dataset.handle = `${scentVariant.handle}?variant=${scentVariant.id}`;
            } else {
              input.disabled = true;
            }
          } else {
            // wait until the size is selected to check if the scent is available : check line 1392
            option.disabled = true;
          }
        });
      } else {
        // keep the selected variant in the URL so "sell when out of stock" variants don't fall back to the first in-stock one
        input.dataset.handle = `${variant.handle}?variant=${variant.id}`;
      }
      if (isSelected) input.checked = true;
      if (!available) input.disabled = true;

      const label = document.createElement('label');
      label.htmlFor = inputId;
      label.textContent = translatedSize;
      if (!available) {
        const span = document.createElement('span');
        span.className = 'visually-hidden label-unavailable';
        span.textContent =
          window.accessibilityStrings?.variantSoldOutOrUnavailable || 'Variant sold out or unavailable';
        label.appendChild(span);
      }

      pillFieldset.appendChild(input);
      pillFieldset.appendChild(label);
    });

    // check if we need to keep option scent disabled
    dropdownSelect.querySelectorAll('option').forEach((option) => {
      if (option.selected === false) {
        const scentVariant = allVariants.find((v) => v.option1 === selectedSize && v.option2 === option.value);
        if (scentVariant) {
          if (scentVariant.available) {
            option.disabled = false;
          } else {
            // change option vlue to unavailable
            option.textContent = `${option.value} - Out of stock`;
          }
        } else {
          // remove option from the select
          option.remove();
          // don't display the select scent if there is only one option left
          if (dropdownSelect.querySelectorAll('option').length === 1) {
            dropdownSelect.parentElement.classList.add('d-none');
          }
        }
      }
    });

    // Après avoir mis à jour les options natives, synchronisez les custom selects
    this.querySelectorAll('.native-select').forEach((select) => {
      this.updateCustomSelectDisplay(select);
    });
  }

  getTranslatedSize(size) {
    const ecorefill = this.querySelector('.ecorefill')?.textContent?.trim();
    if (size.includes('ECO-REFILL') && ecorefill) {
      return size.replace('ECO-REFILL', ecorefill);
    }
    return size;
  }

  getSwatchUrlForScent(variantId) {
    const product = this.productsData.find((p) => p.variants.some((v) => v.id === variantId));
    return product?.images[0] ? `${product.images[0]}?width=50` : 'unset';
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');

      const selectedOption = target.selectedOptions[0];
      selectedOption.setAttribute('selected', 'selected');
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  // custom code start: this getter was missing, so `option_values` was never sent on the section fetch.
  // For products WITHOUT a master code (e.g. gift cards) there is no data-handle navigation, so the fetch
  // fell back to selected_or_first_available_variant -> the clicked out-of-stock variant snapped back to
  // the first in-stock one. Collecting the checked option value ids fixes it. Master-code products are
  // unaffected: they navigate via window.location.href (data-handle) before any fetch runs.
  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }
  // custom code end

  async fetchProductsData() {
    let productData = [];
    const masterCode = this.querySelector('.master-code').textContent;
    const collectionUrl = this.querySelector('.collection-url').textContent;

    if (masterCode && collectionUrl) {
      const response = await fetch(`${collectionUrl}/${masterCode}?sections=occ-variants-data`);
      if (!response.ok || response.redirected) {
        console.log('Empty response or redirect detected');
        return [];
      }
      const text = await response.text();
      const responseJSON = JSON.parse(text);
      const htmlString = responseJSON['occ-variants-data'];
      if (!htmlString) {
        console.log('Empty htmlString detected');
        return [];
      } else {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const scriptElements = doc.querySelectorAll('script[id="ProductJSON"]');

        scriptElements.forEach((script) => {
          const jsonString = script.textContent.trim();
          if (jsonString && jsonString !== 'null') {
            try {
              const data = JSON.parse(jsonString);
              if (data) {
                productData.push(data);
              }
            } catch (error) {
              console.error('Error parsing JSON:', error);
            }
          }
        });
      }
    }
    const updatedProducts = productData.map((p) => ({
      ...p,
      variants: p.variants?.map((v) => ({ ...v, handle: p.handle })) || [],
    }));

    return updatedProducts;
  }
}
customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

class FooterCollapsible {
  constructor() {
    this.mediaQuery = window.matchMedia('(max-width: 989px)');
    this.init();
  }

  // Focusable selectors inside collapsible content
  getFocusableChildren(content) {
    return content.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
  }

  setChildrenTabbable(content, isTabbable) {
    this.getFocusableChildren(content).forEach((el) => {
      if (isTabbable) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  init() {
    if (this.mediaQuery.matches) {
      this.setupCollapsibles();
    }

    this.mediaQuery.addListener((e) => {
      if (e.matches) {
        this.setupCollapsibles();
      } else {
        this.destroyCollapsibles();
      }
    });
  }

  setupCollapsibles() {
    this.collapsibles = document.querySelectorAll('[data-collapsible]');

    this.collapsibles.forEach((item) => {
      item.classList.add('is-collapsed');
      const trigger = item.querySelector('[data-collapsible-trigger]');
      const content = item.querySelector('[data-collapsible-content]');
      const quickLinks = document.querySelector('[data-height]');

      // Skip if no content panel found
      if (!content) return;

      // Disable children immediately on setup since all start collapsed
      this.setChildrenTabbable(content, false);

      const clickHandler = () => {
        item.classList.toggle('is-collapsed');
        const isCollapsed = item.classList.contains('is-collapsed');
        content.style.maxHeight = isCollapsed
          ? 0
          : content.classList.contains('footer__list-social')
            ? '250px'
            : `${content.scrollHeight}px`;

        this.setChildrenTabbable(content, !isCollapsed);

        if (trigger) {
          trigger.setAttribute('aria-expanded', String(!isCollapsed));
        }

        if (!isCollapsed) {
          // Hide the plus and show the minus of the first child of this item
          item.querySelector('.plus').classList.add('medium-hide');
          item.querySelector('.plus').classList.add('small-hide');
          item.querySelector('.minus').classList.remove('medium-hide');
          item.querySelector('.minus').classList.remove('small-hide');
          quickLinks.classList.remove('medium-h-3');
          quickLinks.classList.remove('small-h-3');
        } else {
          item.querySelector('.plus').classList.remove('medium-hide');
          item.querySelector('.plus').classList.remove('small-hide');
          item.querySelector('.minus').classList.add('medium-hide');
          item.querySelector('.minus').classList.add('small-hide');
          quickLinks.classList.add('medium-h-3');
          quickLinks.classList.add('small-h-3');
        }
      };

      item._clickHandler = clickHandler;
      if (trigger) trigger.addEventListener('click', clickHandler);
    });
  }

  destroyCollapsibles() {
    if (this.collapsibles) {
      this.collapsibles.forEach((item) => {
        item.classList.remove('is-collapsed');
        const trigger = item.querySelector('[data-collapsible-trigger]');
        if (trigger) {
          trigger.removeEventListener('click', item._clickHandler);
          trigger.removeAttribute('aria-expanded');
        }

        const content = item.querySelector('[data-collapsible-content]');
        if (content) {
          content.style.maxHeight = '';

          // Restore all children to naturally tabbable on desktop
          this.setChildrenTabbable(content, true);
        }
      });
    }
  }
}
document.addEventListener('DOMContentLoaded', () => {
  new FooterCollapsible();
});

class SidePanel extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.side-panel__overlay');
    this.content = this.querySelector('.side-panel__content');
    this.closeBtn = this.querySelector('.side-panel__close');

    this.hide = this.hide.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    // Déplacement safe
    if (!document.body.contains(this)) {
      document.body.appendChild(this);
    }

    // Ajout des listeners
    const closeBtn = this.querySelector('.side-panel__close');
    const overlay = this.querySelector('.side-panel__overlay');

    if (closeBtn) closeBtn.addEventListener('click', () => this.hide());
    if (overlay) overlay.addEventListener('click', () => this.hide());
  }

  show() {
    this.setAttribute('open', '');
    document.body.classList.add('side-panel--open');
    this.focus();
    trapFocus?.(this, this.content);
    document.querySelector('.shopify-section-group-header-group')?.style?.setProperty('z-index', '1');
    const overlay = document.createElement('div');
    overlay.className = 'panel-overlay';
    document.body.appendChild(overlay);
    document.querySelector('header').appendChild(overlay);
    overlay.style.display = 'block';
    overlay.addEventListener('click', () => this.hide());
    this.closeBtn.style.setProperty('display', 'block');
  }

  hide() {
    this.removeAttribute('open');
    document.body.classList.remove('side-panel--open');
    this.dispatchEvent(new CustomEvent('sidepanelClosed'));
    document.querySelector('.shopify-section-group-header-group')?.style?.setProperty('z-index', '4');
    document.querySelectorAll('.panel-overlay').forEach((overlay) => {
      overlay.remove();
    });
    this.closeBtn.style.setProperty('display', 'none');
  }

  handleKeyup(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.hide();
  }
}

customElements.define('side-panel', SidePanel);

/** Bind play/pause for panel media using the same pattern as `sections/content-media.liquid`. */
function initPanelContentMediaVideos(container) {
  if (!container) return;
  container.querySelectorAll('[data-panel-video-root]').forEach((root) => {
    const video = root.querySelector('video');
    const playButton = root.querySelector('[data-video-action="play"]');
    const pauseButton = root.querySelector('[data-video-action="pause"]');
    if (!video || !playButton || !pauseButton) return;

    const syncButtons = () => {
      const isPlaying = !video.paused;
      playButton.hidden = isPlaying;
      pauseButton.hidden = !isPlaying;
    };

    playButton.addEventListener('click', () => {
      video.play();
    });

    pauseButton.addEventListener('click', () => {
      video.pause();
    });

    video.addEventListener('play', syncButtons);
    video.addEventListener('pause', syncButtons);
    syncButtons();
  });
}

document.querySelectorAll('.panel-trigger').forEach((button) => {
  button.addEventListener('click', () => {
    const panelId = button.getAttribute('data-panel');
    const template = document.querySelector(`#panel-content-${panelId}`);
    const target = document.querySelector('#side-panel-dynamic-content');
    const sidePanelContent = document.querySelector('#infoPanel .side-panel__content');

    if (template && target) {
      target.innerHTML = ''; // clean previous content
      target.appendChild(template.content.cloneNode(true));
      initPanelContentMediaVideos(target);
      if (sidePanelContent) sidePanelContent.scrollTop = 0;
      document.querySelector('#infoPanel').show();
    }
  });
});

/** Fallbacks when DOM sources are missing — mirror layout/theme.liquid `inline_asset_content` markup. */
const SIDE_PANEL_NESTED_CLOSE_ICON_HTML_FALLBACK =
  '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="#3f2b2e" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg></span>';
const SIDE_PANEL_NESTED_BACK_ICON_HTML_FALLBACK =
  '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg></span>';

function escapePanelText(value) {
  if (!value) return '';
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function removeNestedPanel(nestedPanel) {
  if (!nestedPanel) return;
  nestedPanel.classList.remove('is-visible');
  nestedPanel.classList.add('is-closing');
  nestedPanel.addEventListener('transitionend', () => nestedPanel.remove(), { once: true });
  window.setTimeout(() => {
    if (nestedPanel.isConnected) nestedPanel.remove();
  }, 320);
}

const SIDE_PANEL_NESTED_ITEM_SECTION_ID = 'side-panel-nested-item';

function productPageUrlForHandle(handle) {
  const root = window.Shopify?.routes?.root || '/';
  const base = new URL(root, window.location.origin).href;
  return new URL(`products/${encodeURIComponent(handle)}`, base).toString();
}

async function fetchSidePanelNestedItemSectionHtml(handle, variantId) {
  const fetchUrl = new URL(productPageUrlForHandle(handle));
  fetchUrl.searchParams.set('sections', SIDE_PANEL_NESTED_ITEM_SECTION_ID);
  if (variantId) fetchUrl.searchParams.set('variant', String(variantId));

  const response = await fetch(fetchUrl.toString());
  if (!response.ok) throw new Error(`Section fetch ${response.status}`);
  const data = await response.json();
  const html = data[SIDE_PANEL_NESTED_ITEM_SECTION_ID];
  if (!html) throw new Error('Section missing in response');
  return html;
}

function setNestedPanelBodyFromSectionHtml(nestedBody, sectionHtml) {
  const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const sectionEl = doc.querySelector('[id^="shopify-section-"]');
  if (sectionEl) {
    sectionEl.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
    nestedBody.innerHTML = sectionEl.innerHTML;
  } else {
    nestedBody.innerHTML = sectionHtml;
  }
}

async function openWhatsInsideNestedPanel(cardButton) {
  const sidePanelContent = document.querySelector('#infoPanel .side-panel__content');
  if (!sidePanelContent) return;

  const activeNestedPanel = sidePanelContent.querySelector('.side-panel__nested');
  if (activeNestedPanel) removeNestedPanel(activeNestedPanel);

  const itemTitle = cardButton.dataset.itemTitle || '';
  const itemSku = cardButton.dataset.itemSku || '';
  const itemHandle = cardButton.dataset.itemHandle || '';
  const itemVariantId = cardButton.dataset.itemVariantId || '';
  const showEyebrows = cardButton.dataset.itemShowEyebrows === 'true';
  const showRating = cardButton.dataset.itemShowRating === 'true';

  const nestedCloseIconHtml =
    document.querySelector('#infoPanel .side-panel__close')?.innerHTML?.trim() ??
    SIDE_PANEL_NESTED_CLOSE_ICON_HTML_FALLBACK;
  const nestedBackIconHtml =
    document.querySelector('#side-panel-nested-back-icon-source')?.innerHTML?.trim() ??
    SIDE_PANEL_NESTED_BACK_ICON_HTML_FALLBACK;

  const nestedPanel = document.createElement('div');
  nestedPanel.className = 'side-panel__nested';
  const visibleOffsetTop = sidePanelContent.scrollTop;
  const visibleHeight = sidePanelContent.clientHeight;
  nestedPanel.style.top = `${visibleOffsetTop}px`;
  nestedPanel.style.height = `${visibleHeight}px`;

  nestedPanel.innerHTML = `
    <div class="side-panel__nested-header">
      <button type="button" class="side-panel__nested-back" aria-label="Go back">
        ${nestedBackIconHtml}
      </button>
      <h5 class="side-panel__nested-title">${escapePanelText(itemTitle || itemSku || 'Product detail')}</h5>
      <button type="button" class="side-panel__nested-close" aria-label="Close panel">
        ${nestedCloseIconHtml}
      </button>
    </div>
    <div class="side-panel__nested-body">
      <p class="side-panel-nested-item__loading">Loading…</p>
    </div>
  `;

  sidePanelContent.appendChild(nestedPanel);
  window.requestAnimationFrame(() => nestedPanel.classList.add('is-visible'));

  nestedPanel
    .querySelector('.side-panel__nested-back')
    ?.addEventListener('click', () => removeNestedPanel(nestedPanel));
  nestedPanel.querySelector('.side-panel__nested-close')?.addEventListener('click', () => {
    document.querySelector('#infoPanel')?.hide();
  });

  const nestedBody = nestedPanel.querySelector('.side-panel__nested-body');
  if (!nestedBody) return;

  if (!itemHandle) {
    nestedBody.innerHTML =
      '<p class="side-panel-nested-item__loading" role="alert">This item is not linked to a product.</p>';
    return;
  }

  try {
    const sectionHtml = await fetchSidePanelNestedItemSectionHtml(itemHandle, itemVariantId);
    setNestedPanelBodyFromSectionHtml(nestedBody, sectionHtml);
    if (!showEyebrows) {
      nestedBody.querySelectorAll('.product-eyebrow').forEach((eyebrow) => eyebrow.remove());
    }
    if (!showRating) {
      nestedBody
        .querySelectorAll('.rating-wrapper.side-panel-nested-item__reviews')
        .forEach((reviews) => reviews.remove());
    }
  } catch (err) {
    console.error('[side-panel-nested-item]', err);
    nestedBody.innerHTML =
      '<p class="side-panel-nested-item__loading" role="alert">Unable to load product details.</p>';
  }
}

document.addEventListener('click', (event) => {
  const cardButton = event.target.closest('.js-whats-inside-card');
  if (!cardButton) return;
  event.preventDefault();
  void openWhatsInsideNestedPanel(cardButton);
});

document.querySelector('#infoPanel')?.addEventListener('sidepanelClosed', () => {
  document.querySelectorAll('#infoPanel .side-panel__nested').forEach((nestedPanel) => nestedPanel.remove());
});

/*
  Judge.me clone slider — Accessible (WCAG) + no auto-rotate + keyboard nav (Tab, Enter/Space, ← →)
  - Waits for Judge.me injection via MutationObserver
  - Hides original carousel (display:none) and clones visual cards
  - Adds aria, roles, tabindex, keyboard handlers
  - Announces "Slide X of Y. [Reviewer] — [excerpt]" via aria-live
*/

(function () {
  // helper : wait for .jdgm-rev-snippet-card (MutationObserver)
  function waitForJudgeMeCard() {
    return new Promise((resolve) => {
      if (document.querySelector('.jdgm-rev-snippet-card')) return resolve();
      const obs = new MutationObserver(() => {
        if (document.querySelector('.jdgm-rev-snippet-card')) {
          obs.disconnect();
          resolve();
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    });
  }

  // small helper sleep
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  (async function initJudgeMeClone() {
    // paranoia : deactivate auto rotation now
    try {
      window.jdgm = window.jdgm || {};
      window.jdgm.auto_rotate = false;
    } catch (e) {}

    // wait for Judge.me cards
    await waitForJudgeMeCard();
    // additional delay to ensure full markup/styling applied by Judge.me
    await sleep(500);

    const originalContainer = document.querySelector('.jdgm-rev-snippet-widget__cards-container');
    const prevBtn = document.querySelector('.jdgm-rev-snippet-widget__prev-btn');
    const nextBtn = document.querySelector('.jdgm-rev-snippet-widget__next-btn');
    if (!originalContainer || !prevBtn || !nextBtn) {
      console.warn('Judge.me : error container or nav buttons not found');
      return;
    }

    // Save the aria-label values before we modify anything
    const prevAriaLabel = prevBtn.getAttribute('aria-label');
    const nextAriaLabel = nextBtn.getAttribute('aria-label');

    // Remove ALL ARIA attributes from the original elements
    // and add aria-hidden="true" so screen readers ignore them
    [prevBtn, nextBtn].forEach((btn) => {
      // Remove all ARIA attributes
      Array.from(btn.attributes).forEach((attr) => {
        if (attr.name.startsWith('aria-') || attr.name === 'role') {
          btn.removeAttribute(attr.name);
        }
      });
      // Mark as hidden from assistive technology
      btn.setAttribute('aria-hidden', 'true');
    });

    // Find the widget container (parent of both cards and buttons)
    const widgetContainer =
      originalContainer.closest('.jdgm-rev-snippet-widget') ||
      originalContainer.closest('[class*="jdgm"][class*="widget"]') ||
      originalContainer.parentElement;

    // get all visible cards
    const originalCards = Array.from(document.querySelectorAll('.jdgm-rev-snippet-card'));
    if (originalCards.length === 0) {
      console.warn('Judge.me : no cards found');
      return;
    }

    // inject minimal accessibility CSS for focus & disabled state
    const style = document.createElement('style');
    style.textContent = `
      /* visible focus for keyboard users */
      .jdgm-static-card:focus {
        outline: 1px solid #3f2b2e;
        outline-offset: 1px;
      }
      /* disabled nav styling */
      .jdgm-nav-disabled {
        opacity: 0.4;
        pointer-events: none;
      }
      /* visually-hidden utility */
      .sr-only { 
        position: absolute !important; 
        height: 1px; 
        width: 1px; 
        overflow: hidden; 
        clip: rect(1px,1px,1px,1px); 
        white-space: nowrap; 
        border: 0; 
        padding: 0; 
        margin: -1px; 
      }
      /* visually-hidden utility */
      .visually-hidden {
        position: absolute !important;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
      /* navigation buttons wrapper */
      .jdgm-static-nav-buttons {
        display: flex;
        justify-content: center;
        gap: 10px;
        margin-top: 10px;
      }
    `;
    document.head.appendChild(style);

    // create cloned container managed by us
    const cloneWrapper = document.createElement('div');
    cloneWrapper.className = 'jdgm-static-cards-container';
    cloneWrapper.id = 'jdgm-static-slider';
    cloneWrapper.style.overflow = 'hidden';
    cloneWrapper.style.position = 'relative';
    cloneWrapper.style.width = '100%';
    // accessibility
    cloneWrapper.setAttribute('role', 'region');
    cloneWrapper.setAttribute(
      'aria-label',
      cloneWrapper.dataset.reviewsLabel || window.accessibilityStrings?.customerReviews || 'Customer reviews',
    );
    cloneWrapper.setAttribute('tabindex', '0');

    // create live region for announcements
    const live = document.createElement('div');
    live.className = 'sr-only';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    live.id = 'jdgm-static-live';
    cloneWrapper.appendChild(live);

    const inner = document.createElement('div');
    inner.style.display = 'flex';
    inner.style.transition = 'transform 300ms ease';
    inner.style.willChange = 'transform';
    inner.style.transform = 'translateX(0)';
    cloneWrapper.appendChild(inner);

    // clone cards
    let clones = [];
    const originalCount = originalCards.length;

    clones = originalCards.map((origCard, i) => {
      const cloneCard = document.createElement('div');

      cloneCard.className = 'jdgm-static-card jdgm-rev-snippet-card';
      cloneCard.style.flex = '0 0 auto';
      cloneCard.style.boxSizing = 'border-box';

      const reviewId = origCard.getAttribute('data-review-id') || '';
      cloneCard.setAttribute('data-review-id', reviewId);

      // create content wrapper
      const cloneContent = document.createElement('div');
      cloneContent.className = 'jdgm-rev-snippet-card__rev-content-wrapper';

      const content = origCard.querySelector('.jdgm-rev-snippet-card__rev-content-wrapper');
      cloneContent.innerHTML = content ? content.innerHTML : origCard.innerHTML;

      if (cloneContent.firstElementChild) {
        cloneContent.firstElementChild.classList.add('jdgm-static-card-gap');
      }

      cloneCard.appendChild(cloneContent);

      // add accessibility attributes for slide
      cloneCard.setAttribute('tabindex', '0');
      cloneCard.setAttribute('role', 'group');
      cloneCard.setAttribute('aria-roledescription', window.accessibilityStrings?.slideRoledescription || 'slide');
      cloneCard.setAttribute(
        'aria-label',
        (window.accessibilityStrings?.reviewNOfTotal || 'Review [n] of [total]')
          .replace('[n]', i + 1)
          .replace('[total]', originalCount),
      );

      const width = origCard.getBoundingClientRect().width || originalContainer.getBoundingClientRect().width;
      cloneCard.style.minWidth = `${width}px`;

      cloneCard.addEventListener('click', (e) => {
        const selector = `.jdgm-rev-snippet-card[data-review-id="${reviewId}"] .jdgm-rev-snippet-card__full-review-link`;
        const origFullLink = document.querySelector(selector);
        if (origFullLink) origFullLink.click();
      });

      cloneCard.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const selector = `.jdgm-rev-snippet-card[data-review-id="${reviewId}"] .jdgm-rev-snippet-card__full-review-link`;
          const origFullLink = document.querySelector(selector);
          if (origFullLink) origFullLink.click();
        }
      });

      inner.appendChild(cloneCard);
      return {
        el: cloneCard,
        orig: origCard,
        reviewId: reviewId,
        reviewer:
          (
            origCard.querySelector('.jdgm-rev-snippet-card__reviewer') ||
            origCard.querySelector('.jdgm-full-rev__reviewer-name') ||
            {}
          ).textContent?.trim() || '',
        excerpt:
          (
            origCard.querySelector('.jdgm-rev-snippet-card__rev-body') ||
            origCard.querySelector('.jdgm-full-rev__body') ||
            {}
          ).textContent?.trim() || '',
      };
    });

    // Initialize visibility for all slides except the first
    clones.forEach((clone, idx) => {
      if (idx !== 0) {
        clone.el.setAttribute('aria-hidden', 'true');

        // Find ALL focusable elements within this slide
        const focusableElements = clone.el.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), area[href], details, iframe, object, embed, [contenteditable="true"], audio[controls], video[controls]',
        );

        // Make all focusable elements non-focusable
        focusableElements.forEach((el) => {
          // Store original tabindex if it exists
          if (!el.hasAttribute('data-original-tabindex')) {
            el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '');
          }
          el.setAttribute('tabindex', '-1');

          // For interactive elements, also disable them temporarily
          if (
            el.tagName === 'BUTTON' ||
            el.tagName === 'INPUT' ||
            el.tagName === 'SELECT' ||
            el.tagName === 'TEXTAREA'
          ) {
            el.setAttribute('disabled', '');
            el.setAttribute('data-originally-disabled', '');
          }

          // For links, remove href temporarily
          if (el.tagName === 'A' && el.hasAttribute('href')) {
            el.setAttribute('data-original-href', el.getAttribute('href'));
            el.removeAttribute('href');
          }
        });
      }
    });

    // Create accessible nav buttons (these are NEW elements, not clones of old ones)
    function createAccessibleNavButton(originalI, label) {
      const button = document.createElement('button');
      button.type = 'button';
      // Copy only the CSS classes, not ARIA attributes
      button.className = originalI.className;
      button.setAttribute('aria-label', label);
      // Copy the inner HTML (icon)
      button.innerHTML = originalI.innerHTML;
      // Copy any data attributes
      Array.from(originalI.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) {
          button.setAttribute(attr.name, attr.value);
        }
      });
      return button;
    }

    const prevLabel = prevAriaLabel || window.accessibilityStrings?.previousReview || 'Previous review';
    const nextLabel = nextAriaLabel || window.accessibilityStrings?.nextReview || 'Next review';
    const replacementPrev = createAccessibleNavButton(prevBtn, prevLabel);
    const replacementNext = createAccessibleNavButton(nextBtn, nextLabel);

    // Insert the clone wrapper BEFORE the widget container (completely outside it)
    widgetContainer.parentNode.insertBefore(cloneWrapper, widgetContainer);

    // Create buttons wrapper and insert AFTER the clone wrapper, OUTSIDE the widget
    const buttonsWrapper = document.createElement('div');
    buttonsWrapper.className = 'jdgm-static-nav-buttons';
    buttonsWrapper.appendChild(replacementPrev);
    buttonsWrapper.appendChild(replacementNext);

    // Insert buttons wrapper right after the clone wrapper (still outside widget)
    cloneWrapper.parentNode.insertBefore(buttonsWrapper, cloneWrapper.nextSibling);

    const prevBtnEl = replacementPrev;
    const nextBtnEl = replacementNext;

    // Now completely hide the original widget container and its contents
    widgetContainer.style.display = 'none';
    // Also set aria-hidden on the widget to ensure screen readers ignore it
    widgetContainer.setAttribute('aria-hidden', 'true');

    // Specifically hide the original buttons too (though they're in the hidden widget)
    prevBtn.style.display = 'none';
    nextBtn.style.display = 'none';

    // navigation state
    let currentIndex = 0;
    const calcCardWidth = () =>
      clones[0].el.getBoundingClientRect().width || originalContainer.getBoundingClientRect().width;
    let cardWidth = calcCardWidth();
    const maxIndex = Math.max(0, clones.length - 1);

    const goTo = (index, announce = true) => {
      currentIndex = Math.max(0, Math.min(index, maxIndex));
      const x = -currentIndex * cardWidth;
      inner.style.transform = `translateX(${x}px)`;
      updateButtons();
      updateSlideVisibility();
      try {
        clones[currentIndex].el.focus({ preventScroll: true });
      } catch (e) {}
      if (announce) announceSlide(currentIndex);
    };

    function announceSlide(idx) {
      const meta = clones[idx];
      const position = `Slide ${idx + 1} of ${clones.length}.`;
      const excerpt = (meta.excerpt || '').trim().replace(/\s+/g, ' ');
      const reviewer = (meta.reviewer || '').trim();
      const excerptShort = excerpt.length > 140 ? excerpt.slice(0, 137) + '…' : excerpt;
      const parts = [position];
      if (reviewer) parts.push(`Review by ${reviewer}.`);
      if (excerptShort) parts.push(excerptShort);
      const message = parts.join(' ');
      const liveEl = document.getElementById('jdgm-static-live');
      if (liveEl) {
        liveEl.textContent = message;
      }
    }

    function updateSlideVisibility() {
      clones.forEach((clone, idx) => {
        const isActive = idx === currentIndex;

        // Set visibility
        clone.el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        clone.el.setAttribute('tabindex', isActive ? '0' : '-1');

        // Find all focusable elements
        const focusableElements = clone.el.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), area[href], details, iframe, object, embed, [contenteditable="true"], audio[controls], video[controls], [data-original-href]',
        );

        if (!isActive) {
          // Hide from screen readers and make non-interactive
          focusableElements.forEach((el) => {
            // Store original state
            if (!el.hasAttribute('data-original-tabindex')) {
              el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '');
            }
            el.setAttribute('tabindex', '-1');

            // Disable interactive elements
            if (
              el.tagName === 'BUTTON' ||
              el.tagName === 'INPUT' ||
              el.tagName === 'SELECT' ||
              el.tagName === 'TEXTAREA'
            ) {
              if (!el.hasAttribute('data-originally-disabled')) {
                el.setAttribute('data-originally-disabled', el.disabled ? 'true' : 'false');
              }
              el.setAttribute('disabled', '');
            }

            // Remove href from links
            if (el.tagName === 'A' && el.hasAttribute('href')) {
              el.setAttribute('data-original-href', el.getAttribute('href'));
              el.removeAttribute('href');
            }
          });
        } else {
          // Make interactive again
          focusableElements.forEach((el) => {
            // Restore tabindex
            const originalTabindex = el.getAttribute('data-original-tabindex');
            if (originalTabindex !== null) {
              if (originalTabindex === '') {
                el.removeAttribute('tabindex');
              } else {
                el.setAttribute('tabindex', originalTabindex);
              }
              el.removeAttribute('data-original-tabindex');
            }

            // Re-enable interactive elements that weren't originally disabled
            if (el.hasAttribute('data-originally-disabled')) {
              if (el.getAttribute('data-originally-disabled') === 'false') {
                el.removeAttribute('disabled');
              }
              el.removeAttribute('data-originally-disabled');
            }

            // Restore href for links
            if (el.hasAttribute('data-original-href')) {
              el.setAttribute('href', el.getAttribute('data-original-href'));
              el.removeAttribute('data-original-href');
            }
          });
        }
      });
    }

    function updateButtons() {
      const isFirst = currentIndex === 0;
      const isLast = currentIndex === maxIndex;

      if (isFirst) {
        prevBtnEl.setAttribute('disabled', '');
        prevBtnEl.classList.add('jdgm-nav-disabled');
      } else {
        prevBtnEl.removeAttribute('disabled');
        prevBtnEl.classList.remove('jdgm-nav-disabled');
      }

      if (isLast) {
        nextBtnEl.setAttribute('disabled', '');
        nextBtnEl.classList.add('jdgm-nav-disabled');
      } else {
        nextBtnEl.removeAttribute('disabled');
        nextBtnEl.classList.remove('jdgm-nav-disabled');
      }
    }

    function isAndroid() {
      return navigator.userAgentData?.platform === 'Android' || /Android/i.test(navigator.userAgent);
    }

    nextBtnEl.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      goTo(currentIndex + 1);
      try {
        window.jdgm = window.jdgm || {};
        window.jdgm.auto_rotate = false;
      } catch (err) {}
    });

    prevBtnEl.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      goTo(currentIndex - 1);
      try {
        window.jdgm = window.jdgm || {};
        window.jdgm.auto_rotate = false;
      } catch (err) {}
    });

    cloneWrapper.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(currentIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(currentIndex - 1);
      }
    });

    window.addEventListener('resize', () => {
      const metaProduct = meta?.product;
      if (metaProduct && isAndroid()) {
        return;
      }

      cardWidth = calcCardWidth();
      clones.forEach((c) => (c.el.style.minWidth = `${cardWidth}px`));
      goTo(currentIndex, false);
    });

    const guardInterval = setInterval(() => {
      try {
        window.jdgm = window.jdgm || {};
        window.jdgm.auto_rotate = false;
      } catch (e) {}
    }, 2000);

    updateButtons();
    updateSlideVisibility();
    await sleep(100);
    announceSlide(0);

    console.log('Judge.me clone initialized — auto-rotate disabled and accessible custom navigation active.');

    // Enhance Judge.me modals and interactive elements with proper ARIA attributes
    (function enhanceJudgeMeA11y() {
      const modalObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== Node.ELEMENT_NODE) continue;

            // --- Fix any elements with prohibited ARIA combinations ---
            const prohibitedElements = [];
            if (node.matches?.('[role="img"][aria-label]')) {
              prohibitedElements.push(node);
            }
            if (node.querySelectorAll) {
              const found = node.querySelectorAll('[role="img"][aria-label]');
              prohibitedElements.push(...found);
            }

            prohibitedElements.forEach((el) => {
              if (el.dataset.ariaFixed !== 'true') {
                el.dataset.ariaFixed = 'true';
                const labelText = el.getAttribute('aria-label');
                if (labelText) {
                  const labelId = 'img-label-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
                  const labelEl = document.createElement('span');
                  labelEl.id = labelId;
                  labelEl.className = 'visually-hidden';
                  labelEl.textContent = labelText;
                  el.parentNode.insertBefore(labelEl, el);
                  el.removeAttribute('aria-label');
                  el.setAttribute('aria-labelledby', labelId);
                }
              }
            });

            // --- Full review modal ---
            const modal = node.matches?.(
              '.jdgm-rev-modal, .jdgm-rev-modal__wrapper, [class*="jdgm-rev"][class*="modal"]',
            )
              ? node
              : node.querySelector?.('.jdgm-rev-modal, .jdgm-rev-modal__wrapper, [class*="jdgm-rev"][class*="modal"]');
            if (modal && modal.dataset.a11yEnhanced !== 'true') {
              modal.dataset.a11yEnhanced = 'true';
              if (!modal.hasAttribute('role')) modal.setAttribute('role', 'dialog');
              modal.setAttribute('aria-modal', 'true');
              const heading = modal.querySelector(
                'h1, h2, h3, h4, .jdgm-rev__title, .jdgm-rev__author, .jdgm-full-rev__title',
              );
              if (heading && !modal.hasAttribute('aria-labelledby')) {
                if (!heading.id) heading.id = 'jdgm-modal-heading-' + Date.now();
                modal.setAttribute('aria-labelledby', heading.id);
              }
              setTimeout(() => {
                try {
                  modal.focus({ preventScroll: true });
                } catch (e) {}
              }, 150);
            }

            // --- "See all reviews" div with onclick ---
            const seeAllBtn = node.matches?.('[onclick*="review"], [onclick*="jdgm"]')
              ? node
              : node.querySelectorAll?.('[onclick*="review"], [onclick*="jdgm"]');
            const seeAllElements =
              seeAllBtn && seeAllBtn.length !== undefined ? seeAllBtn : seeAllBtn ? [seeAllBtn] : [];
            seeAllElements.forEach((el) => {
              if (el.tagName === 'BUTTON' || el.tagName === 'A' || el.dataset.a11ySeeAll === 'true') return;
              el.dataset.a11ySeeAll = 'true';
              el.setAttribute('role', 'button');
              el.setAttribute('tabindex', '0');
              el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  el.click();
                }
              });
            });

            // --- Ensure #judgeme_product_reviews is focusable ---
            const reviewsAnchor = document.getElementById('judgeme_product_reviews');
            if (reviewsAnchor && !reviewsAnchor.hasAttribute('tabindex')) {
              reviewsAnchor.setAttribute('tabindex', '-1');
            }
          }
        }
      });
      modalObserver.observe(document.body, { childList: true, subtree: true });

      // Check immediately for already-rendered elements
      const existingAnchor = document.getElementById('judgeme_product_reviews');
      if (existingAnchor && !existingAnchor.hasAttribute('tabindex')) {
        existingAnchor.setAttribute('tabindex', '-1');
      }

      // Fix any existing prohibited ARIA combinations immediately
      const existingProhibited = document.querySelectorAll('[role="img"][aria-label]');
      existingProhibited.forEach((el) => {
        if (el.dataset.ariaFixed !== 'true') {
          el.dataset.ariaFixed = 'true';
          const labelText = el.getAttribute('aria-label');
          if (labelText) {
            const labelId = 'img-label-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
            const labelEl = document.createElement('span');
            labelEl.id = labelId;
            labelEl.className = 'visually-hidden';
            labelEl.textContent = labelText;
            el.parentNode.insertBefore(labelEl, el);
            el.removeAttribute('aria-label');
            el.setAttribute('aria-labelledby', labelId);
          }
        }
      });
    })();

    // Enhance star ratings with proper accessibility
    (function enhanceJudgeMeStarsAccessibility() {
      const ratingBlocks = document.querySelectorAll('.jdgm-rev-snippet-card__rev-rating');

      ratingBlocks.forEach((ratingBlock) => {
        const stars = ratingBlock.querySelectorAll('.jdgm-star');
        const score = ratingBlock.getAttribute('data-score');

        if (ratingBlock.dataset.a11yEnhanced === 'true') return;
        ratingBlock.dataset.a11yEnhanced = 'true';

        stars.forEach((star, index) => {
          const starNumber = index + 1;

          const span = document.createElement('span');
          span.className = star.className;

          const hiddenSpan = document.createElement('span');
          hiddenSpan.className = 'visually-hidden';
          hiddenSpan.id = `jdgm-star-${Date.now()}-${starNumber}-${Math.random().toString(36).substr(2, 9)}`;

          const labelKey = starNumber > 1 ? 'starRatingPlural' : 'starRatingSingular';
          const labelTemplate =
            window.accessibilityStrings?.[labelKey] || `${starNumber} star${starNumber > 1 ? 's' : ''}`;
          hiddenSpan.textContent = labelTemplate.replace('[count]', starNumber);

          span.appendChild(hiddenSpan);

          span.setAttribute('role', 'img');
          span.setAttribute('aria-labelledby', hiddenSpan.id);

          star.replaceWith(span);
        });

        if (score) {
          const ratingTemplate = window.accessibilityStrings?.ratingOutOf || 'Rating: [score] out of [total] stars';
          if (ratingBlock.getAttribute('role') === 'img') {
            ratingBlock.removeAttribute('role');
          }
          ratingBlock.setAttribute(
            'aria-label',
            ratingTemplate.replace('[score]', score).replace('[total]', stars.length),
          );
        }
      });
    })();

    // Optional: expose cleanup if needed
    window._jdgmStatic = {
      goToIndex: (i) => goTo(i),
      destroy: () => {
        clearInterval(guardInterval);
        widgetContainer.style.display = '';
        widgetContainer.removeAttribute('aria-hidden');
        prevBtn.style.display = '';
        nextBtn.style.display = '';
        cloneWrapper.remove();
        buttonsWrapper.remove();
      },
    };
  })();
})();

// Restrict phone number input to valid phone characters
function restrictPhoneInput(selector) {
  document.querySelectorAll(selector).forEach((input) => {
    input.addEventListener('input', () => {
      input.value = input.value.replace(/[^0-9+\s().\-]/g, '');
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  restrictPhoneInput('input[type="tel"]');
});
